import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  QueryClient,
  UseMutationResult,
  UseQueryResult,
} from "@tanstack/react-query";

import {
  setDistance,
  setLocation,
  setProcedure,
} from "@/store/price transparency/price-transparency-slice";
import { COVERAGE_CODE, HCBB_DEFAULT_DISTANCE, QueryKeys } from "@/constants";
import { useUserLocation } from "@/context/location-provider";
import { useCachedQueryData } from "@/hooks/use-cached-query-data";
import type { CoveragePlanDatesResponse } from "@/model/coverage-years";
import {
  buildHcbbResultUrl,
  DEFAULT_SELECTION_KIND,
  decodeHcbbSearch,
  HCBB_FACILITY_DETAIL_PATH,
  HCBB_PROVIDER_DETAIL_PATH,
  isHcbbSearchComplete,
  toBatches,
  toHcbbProcedureId,
  toHcbbSearchFromRecent,
  toUniqueById,
} from "@/lib/hcbb-cost-estimator-utils";
import type {
  HcbbCostSearch,
  HcbbSearch,
} from "@/lib/hcbb-cost-estimator-utils";
import {
  hcbbEstimate,
  hcbbFacilityProviders,
  hcbbProfessionals,
  hcbbRecentSearches,
  hcbbSearch,
  RECENT_SEARCHES_SIZE,
} from "@/services/hcbb-cost-estimator";
import type { HcbbMemberInfo } from "@/services/hcbb-cost-estimator";
import useUserInfo from "@/hooks/use-user-info";
import type {
  HcbbFacilityProvidersResponse,
  HcbbFacilityResult,
  HcbbFreeTextSearchResponse,
  HcbbPriceHighlight,
  HcbbProcedureDetailsResponse,
  HcbbProfessionalRef,
  HcbbProfessionalResult,
  HcbbRecentSearch,
  HcbbSearchResultRow,
  HcbbSuggestion,
  HcbbSuggestionGroups,
  HcbbSuggestionKind,
} from "@/model/hcbb-cost-estimator";
import type Place from "@/model/place";
import type { RootState } from "@/store/store";
import {
  INITIAL_HCBB_RESULT_VIEW,
  setHcbbActiveTab,
  setHcbbFilteredVisibleBatches,
  setHcbbVisibleBatches,
  setHcbbVisibleFacilities,
} from "@/store/hcbb-cost-estimator/hcbb-result-view-slice";
import type { HcbbResultTab } from "@/store/hcbb-cost-estimator/hcbb-result-view-slice";
import {
  INITIAL_HCBB_ADVANCED_FILTER_VALUES,
  INITIAL_HCBB_ADVANCED_FILTERS,
  clearHcbbAdvancedFilters,
  setHcbbAdvancedFilters,
  setHcbbAdvancedFiltersOpen,
} from "@/store/hcbb-cost-estimator/hcbb-advanced-filters-slice";
import type { HcbbAdvancedFilters } from "@/store/hcbb-cost-estimator/hcbb-advanced-filters-slice";

/** Response group backing each dropdown tab. */
const SUGGESTION_GROUPS: {
  kind: HcbbSuggestionKind;
  group: keyof HcbbFreeTextSearchResponse;
}[] = [
  { kind: "procedure", group: "procedures" },
  { kind: "doctor", group: "doctors" },
  { kind: "facility", group: "facilities" },
  { kind: "specialty", group: "specialties" },
];

/**
 * `id` is what a pick is later priced by, so it keeps the `${kind}-${id}`
 * shape the results query reads back out of the URL. A row with no label is
 * unpickable, so it is dropped rather than rendered as a blank line.
 */
const toSuggestions = (
  rows: HcbbSearchResultRow[],
  kind: HcbbSuggestionKind,
): HcbbSuggestion[] =>
  rows
    .map((row) => ({
      id: `${kind}-${row.procedureId ?? row.id}`,
      kind,
      label: row.displayName ?? row.name ?? "",
    }))
    .filter((suggestion) => suggestion.label.length > 0);

const toSuggestionGroups = (
  response: HcbbFreeTextSearchResponse,
): HcbbSuggestionGroups =>
  SUGGESTION_GROUPS.reduce(
    (groups, { kind, group }) => ({
      ...groups,
      [kind]: toSuggestions(response[group], kind),
    }),
    {} as HcbbSuggestionGroups,
  );

/**
 * `userInfo` plus the medical coverage period HCBB prices against.
 *
 * HCBB only ever prices medical procedures, so this reads the MED entry off
 * the coverage-years cache specifically — not whichever coverage type the
 * member has active elsewhere in the app — and takes its first period. The
 * cache is populated at login, keyed by username; before it lands, both dates
 * are empty rather than missing.
 */
const useHcbbMemberInfo = (): HcbbMemberInfo => {
  const userInfo = useUserInfo();
  const coverageYears = useCachedQueryData<CoveragePlanDatesResponse>([
    QueryKeys.COVERAGE_YEARS,
    userInfo.username,
  ]);

  const medicalPeriod = coverageYears?.coverages.find(
    (coverage) => coverage.type === COVERAGE_CODE.MEDICAL,
  )?.periods[0];

  return {
    ...userInfo,
    coverageStartDate: medicalPeriod?.startDate ?? "",
    coverageEndDate: medicalPeriod?.endDate ?? "",
  };
};

/**
 * Free-text search for the cost-estimator dropdown.
 *
 * A mutation rather than a query because the dropdown fires it per keystroke
 * (debounced) and nothing outside the open dropdown reads the result — there
 * is no cache entry worth keeping per term. The endpoint matches the term
 * server-side, so the response needs no further filtering.
 */
export const useHcbbFreeTextSearch = (): UseMutationResult<
  HcbbSuggestionGroups,
  Error,
  { queryString: string }
> => {
  const userInfo = useHcbbMemberInfo();

  return useMutation<HcbbSuggestionGroups, Error, { queryString: string }>({
    mutationKey: [QueryKeys.HCBB_SEARCH, userInfo.memberId],
    mutationFn: async ({ queryString }) =>
      toSuggestionGroups(await hcbbSearch(queryString, userInfo)),
  });
};

/* ── cost estimate ───────────────────────────────────────────────────────── */

/**
 * The estimate is the slow call of the pair and its answer is large, so it is
 * cached — but for minutes, not for the session. The app-wide default is
 * `staleTime: Infinity`, which would pin a member's first estimate in place
 * for as long as the tab stayed open.
 */
const ESTIMATE_STALE_TIME_MS = 5 * 60 * 1000;
const ESTIMATE_GC_TIME_MS = 15 * 60 * 1000;

/**
 * The estimate query's cache key, shared with whatever prefetches it ahead
 * of a normal mount (see `useHcbbRecentSearches`'s `rerunSearch`) — a
 * mismatched key here would prefetch into a cache entry the results page
 * never reads, silently wasting the call it was meant to save.
 *
 * All three search fields belong in it: the same procedure priced from a new
 * location or radius is a different result set, and leaving any of them out
 * makes re-running the search from the results form a no-op.
 */
const toHcbbEstimateQueryKey = (
  { procedure, targetLocation, distance }: HcbbCostSearch,
  memberId: string,
) => [
  QueryKeys.HCBB_ESTIMATE,
  memberId,
  procedure?.code,
  targetLocation?.place_id,
  distance?.id,
];

/** Prices a committed search. */
export const useHcbbProcedureDetails = ({
  procedure,
  targetLocation,
  distance,
}: HcbbCostSearch): UseQueryResult<HcbbProcedureDetailsResponse, Error> => {
  const userInfo = useHcbbMemberInfo();
  return useQuery<HcbbProcedureDetailsResponse, Error>({
    queryKey: toHcbbEstimateQueryKey(
      { procedure, targetLocation, distance },
      userInfo.memberId,
    ),
    enabled: Boolean(procedure && targetLocation && userInfo.accessToken),
    staleTime: ESTIMATE_STALE_TIME_MS,
    gcTime: ESTIMATE_GC_TIME_MS,
    queryFn: () =>
      hcbbEstimate({ procedure, targetLocation, distance }, userInfo),
  });
};

/* ── professional details ────────────────────────────────────────────────── */

/**
 * References per request.
 *
 * Not a display choice: the details endpoint degrades badly past ten, which
 * is the reason the estimate answers with references at all.
 */
export const PROFESSIONAL_BATCH_SIZE = 10;

/**
 * Where the reveal depth lives.
 *
 * The hook does not own it: the depth has to outlive this component so a
 * trip to a detail screen and back lands on the same rows, and that is the
 * caller's business. Everything else about batching stays here.
 */
export type HcbbProfessionalsPaging = {
  visibleBatches: number;
  onVisibleBatchesChange: (count: number) => void;
};

export type HcbbProfessionalsList = {
  /** Rows for the batches revealed so far, in reference order. */
  items: HcbbProfessionalResult[];
  /** References the estimate found, including those not yet fetched. */
  totalCount: number;
  /** References View More has still to reveal. */
  remaining: number;
  /** Rows in flight for a batch the member can already see. */
  loadingCount: number;
  /** Set when a revealed batch failed; the rows above it stay on screen. */
  loadMoreError: Error | null;
  /** Reveals the next batch — instant once the batch ahead has landed. */
  viewMore: () => void;
  /** Re-requests the failed batch's ten references, and nothing else. */
  retry: () => void;
};

/**
 * One professional's own cache entry — written by every batch fetch that
 * resolves it, regardless of which batch grouping produced it.
 *
 * This is what lets a filtered (or reset) candidate list reuse a provider
 * already fetched under a *different* grouping: batch-level caching alone
 * (keyed by that batch's own composition) would treat every re-grouping as a
 * cache miss, even for a professional whose data is already known.
 */
const toHcbbProfessionalQueryKey = (
  memberId: string | undefined,
  procedureCode: string | undefined,
  professionalId: string,
) => [QueryKeys.HCBB_PROFESSIONAL, memberId, procedureCode, professionalId];

/**
 * Professional details for a committed search, a batch at a time — sourced
 * from `candidates`, which the caller may pass as the full `/estimate` list
 * or an already-filtered subset of it. The hook itself is agnostic to which;
 * everything below is computed off whatever array it's given.
 *
 * Each batch is its own cache entry rather than a page of one query, which is
 * what lets a failure be retried on its own ten references while the rows
 * above it stay put. One batch past the last visible one is always requested,
 * so View More reveals rows that have already arrived instead of starting a
 * request the member has to wait out.
 *
 * `highlight` is the same search's own priced benchmark — its `majorGroup`
 * and fee breakdown ride along on every batch, unchanged, because they
 * describe the procedure rather than the batch.
 */
export const useHcbbProfessionals = (
  { procedure, targetLocation, distance }: HcbbCostSearch,
  candidates: HcbbProfessionalResult[],
  highlight: HcbbPriceHighlight | null,
  paging: HcbbProfessionalsPaging,
): HcbbProfessionalsList => {
  const userInfo = useHcbbMemberInfo();
  const queryClient = useQueryClient();
  const { visibleBatches, onVisibleBatchesChange } = paging;

  // Batches are chunked from `candidates` directly and never re-chunked based
  // on which of their members happen to already be known — batch composition
  // depending on `known` would reshuffle every later batch the moment one
  // candidate resolved mid-batch, cascading into repeated re-fetches of
  // ever-shifting groups. Stable batches, keyed by their own fixed
  // composition, are what make the per-professional reuse below safe.
  const batches = useMemo(
    () => toBatches(candidates, PROFESSIONAL_BATCH_SIZE),
    [candidates],
  );

  // A disabled query never fetches on its own, but it does stay subscribed
  // to that exact cache entry — so this re-renders the instant any batch,
  // fetched under any grouping, writes that professional's row.
  const knownQueries = useQueries({
    queries: candidates.map((candidate) => ({
      queryKey: toHcbbProfessionalQueryKey(
        userInfo.memberId,
        procedure?.code,
        candidate.professionalId,
      ),
      enabled: false,
      staleTime: ESTIMATE_STALE_TIME_MS,
      gcTime: ESTIMATE_GC_TIME_MS,
    })),
  });

  const known = useMemo(() => {
    const map = new Map<string, HcbbProfessionalResult>();
    candidates.forEach((candidate, index) => {
      const data = knownQueries[index]?.data as
        | HcbbProfessionalResult
        | undefined;
      if (data) map.set(candidate.professionalId, data);
    });
    return map;
  }, [candidates, knownQueries]);

  const fetchedBatches = Math.min(visibleBatches + 1, batches.length);

  const results = useQueries({
    queries: batches.slice(0, fetchedBatches).map((batch) => {
      // A batch every one of whose members is already known (fetched once,
      // under this grouping or a different one) needs no network call at
      // all — this is the actual reuse mechanism. A batch with even one
      // still-unknown member is fetched in full; the handful of already-known
      // rows it re-answers with is a bounded, one-time cost, not a cascade.
      const isFullyKnown = batch.every((candidate) =>
        known.has(candidate.professionalId),
      );

      return {
        // The search identity as well as the references — the same provider
        // priced for another procedure is another answer. No position index:
        // two batches with the same id composition (a common recurrence once
        // a filter round-trips back to the same grouping) are the same query
        // on purpose.
        queryKey: [
          QueryKeys.HCBB_PROFESSIONALS,
          userInfo.memberId,
          procedure?.code,
          targetLocation?.place_id,
          distance?.id,
          batch.map((candidate) => candidate.professionalId),
        ],
        enabled: Boolean(userInfo.accessToken) && !isFullyKnown,
        staleTime: ESTIMATE_STALE_TIME_MS,
        gcTime: ESTIMATE_GC_TIME_MS,
        // Recovered by the member's own Retry rather than by two silent
        // attempts they cannot see — the app-wide default retries twice.
        retry: false,
        // A re-run estimate rebuilds these keys. Holding the rows already
        // rendered keeps the list in place instead of collapsing it.
        placeholderData: (previous?: HcbbProfessionalResult[]) => previous,
        queryFn: async ({ signal }: { signal: AbortSignal }) => {
          const refs: HcbbProfessionalRef[] = batch.map((candidate) => ({
            professionalId: candidate.professionalId,
            npi: candidate.npi,
          }));
          const rows = await hcbbProfessionals(
            { procedure, targetLocation, professionals: refs, highlight },
            userInfo,
            signal,
          );
          // Seeds the per-professional cache above, so this exact data is
          // reused the moment any other grouping of the same candidate needs
          // it — including this same batch re-forming after a filter clears.
          rows.forEach((row) => {
            queryClient.setQueryData(
              toHcbbProfessionalQueryKey(
                userInfo.memberId,
                procedure?.code,
                row.professionalId,
              ),
              row,
            );
          });
          return rows;
        },
      };
    }),
  });

  const visible = results.slice(0, visibleBatches);
  const failed = visible.find((batch) => batch.isError);
  const visibleCount = Math.min(
    visibleBatches * PROFESSIONAL_BATCH_SIZE,
    candidates.length,
  );
  const visibleCandidates = candidates.slice(0, visibleCount);

  return {
    items: toUniqueById(
      visibleCandidates
        .map((candidate) => known.get(candidate.professionalId))
        .filter((row): row is HcbbProfessionalResult => Boolean(row)),
    ),
    totalCount: candidates.length,
    remaining: candidates.length - visibleCount,
    // A count rather than a flag, so the last short batch draws the three
    // rows it will actually fill. `isFetching` without data, not `isPending`:
    // a disabled batch stays pending for ever, and a batch refetching in the
    // background already has rows to show. A fully-known batch is disabled
    // and so never counted here either.
    loadingCount: batches
      .slice(0, visibleBatches)
      .filter((_, index) => visible[index]?.isFetching && !visible[index]?.data)
      .reduce((count, batch) => count + batch.length, 0),
    loadMoreError: failed?.error ?? null,
    viewMore: () =>
      onVisibleBatchesChange(Math.min(visibleBatches + 1, batches.length)),
    retry: () => void failed?.refetch(),
  };
};

/* ── facility providers ───────────────────────────────────────────────────── */

/**
 * The doctors working at one facility, for the facility detail screen.
 *
 * Fired once when the screen opens rather than batched or paged, unlike the
 * professionals tab: a single facility's own staff list is expected to be
 * short enough for one call. `targetLocation` stays in the key because the
 * zip it resolves to changes the price; `distance` does not, because the
 * request never carries it — a facility's own doctors don't reprice by the
 * radius the member searched within. `highlight` is the same estimate's own
 * priced benchmark — its `majorGroup` describes the procedure, not the
 * facility, so it rides along unchanged.
 */
export const useHcbbFacilityProviders = (
  { procedure, targetLocation }: HcbbCostSearch,
  facility: HcbbFacilityResult | null,
  highlight: HcbbPriceHighlight | null,
): UseQueryResult<HcbbFacilityProvidersResponse, Error> => {
  const userInfo = useHcbbMemberInfo();

  return useQuery<HcbbFacilityProvidersResponse, Error>({
    queryKey: [
      QueryKeys.HCBB_FACILITY_PROVIDERS,
      userInfo.memberId,
      procedure?.code,
      targetLocation?.place_id,
      facility?.id,
    ],
    enabled: Boolean(procedure && facility && userInfo.accessToken),
    staleTime: ESTIMATE_STALE_TIME_MS,
    gcTime: ESTIMATE_GC_TIME_MS,
    queryFn: () =>
      hcbbFacilityProviders(
        { procedure, targetLocation, highlight },
        facility?.npis ?? "",
        userInfo,
      ),
  });
};

/**
 * The recent-search row a just-committed search would produce, or null when
 * the search is not actually complete enough to price (mirrors the guard
 * `toRecentSearch` applies to a real response row).
 *
 * The id scheme matches `toRecentSearch`'s in the service exactly, so this
 * locally-built entry reconciles with the real one the next time the list is
 * genuinely refetched, rather than appearing as a duplicate.
 */
const toRecentSearchEntry = (
  { procedure, targetLocation, distance }: HcbbCostSearch,
  userInfo: HcbbMemberInfo,
): HcbbRecentSearch | null => {
  if (!procedure || !targetLocation) return null;

  const procedureId = toHcbbProcedureId(procedure.code);
  const zipCode = targetLocation.postal_code || userInfo.zipCode || "";
  if (!zipCode) return null;

  const distanceValue = distance?.value ?? 0;

  return {
    id: `${procedureId}-${zipCode}-${distanceValue}`,
    procedureId,
    displayName: procedure.description || procedureId,
    zipCode,
    address: targetLocation.description || zipCode,
    distance: distanceValue,
  };
};

/**
 * Puts a search at the top of the cached recent-searches list, in place,
 * without a round trip.
 *
 * Both a fresh form search and re-running a history row call this, so the
 * panel reflects what the member just did the instant they go Back — a
 * `staleTime: 0` refetch here would instead race the server's own
 * not-yet-consistent view of "just now" and reorder the list underneath
 * them, which is the "messed up" list a refetch on every mount produced.
 */
const prependRecentSearch = (
  queryClient: QueryClient,
  memberId: string,
  entry: HcbbRecentSearch,
): void => {
  queryClient.setQueryData<HcbbRecentSearch[]>(
    [QueryKeys.HCBB_RECENT_SEARCH, memberId],
    (current = []) =>
      [entry, ...current.filter((search) => search.id !== entry.id)].slice(
        0,
        RECENT_SEARCHES_SIZE,
      ),
  );
};

type UseHcbbSearch = HcbbSearch & {
  /** True once a suggestion, a location and a distance are all selected. */
  isSearchable: boolean;
  /** Records a dropdown pick of any kind — procedure, doctor, facility, specialty. */
  selectSuggestion: (suggestion: HcbbSuggestion) => void;
  /** Clears the current selection. */
  clearSelection: () => void;
  /** Navigates to the results route with the current search in the URL. */
  submitSearch: () => void;
};

/**
 * Working state for the enhanced cost-estimator form.
 *
 * The priceTransparency slice holds the in-progress selection; the results URL
 * is what a search commits to. On a deep link or a refresh the slice may be
 * empty, so the search is hydrated back out of the URL once on mount.
 */
export const useHcbbSearch = (): UseHcbbSearch => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const hasHydratedRef = useRef(false);
  const userInfo = useHcbbMemberInfo();
  const queryClient = useQueryClient();

  const { procedure, targetLocation, distance } = useSelector(
    (state: RootState) => state.priceTransparency,
  );

  // The slice has nowhere to put the entity type, and it only has to survive
  // until Search is pressed, so it lives here and is seeded from the URL.
  const [selectionKind, setSelectionKind] = useState<HcbbSuggestionKind>(
    () => decodeHcbbSearch(searchParams).selectionKind,
  );

  useEffect(() => {
    if (hasHydratedRef.current) return;
    hasHydratedRef.current = true;

    const searchFromUrl = decodeHcbbSearch(searchParams);
    if (!searchFromUrl.procedure && !searchFromUrl.targetLocation) return;

    // Only fill gaps — never clobber a selection the member just made.
    if (!procedure && searchFromUrl.procedure) {
      dispatch(setProcedure(searchFromUrl.procedure));
    }
    if (!targetLocation && searchFromUrl.targetLocation) {
      dispatch(setLocation(searchFromUrl.targetLocation));
    }
    dispatch(setDistance(searchFromUrl.distance));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const search: HcbbSearch = {
    procedure,
    selectionKind,
    targetLocation,
    distance,
  };

  const selectSuggestion = (suggestion: HcbbSuggestion) => {
    setSelectionKind(suggestion.kind);
    dispatch(
      setProcedure({ code: suggestion.id, description: suggestion.label }),
    );
  };

  const clearSelection = () => {
    setSelectionKind(DEFAULT_SELECTION_KIND);
    dispatch(setProcedure(null));
  };

  const submitSearch = () => {
    if (!isHcbbSearchComplete(search)) return;

    const entry = toRecentSearchEntry(search, userInfo);
    if (entry) prependRecentSearch(queryClient, userInfo.memberId, entry);

    navigate(buildHcbbResultUrl(search), {
      replace: false,
      state: { key: location.key },
    });
  };

  return {
    ...search,
    isSearchable: isHcbbSearchComplete(search),
    selectSuggestion,
    clearSelection,
    submitSearch,
  };
};

/**
 * The committed search — what the results URL actually asked for.
 *
 * Results read this rather than the slice so that editing the form on the
 * results page does not silently swap the results out before Search is pressed.
 */
export const useHcbbCommittedSearch = (): HcbbSearch => {
  const [searchParams] = useSearchParams();

  return useMemo(() => decodeHcbbSearch(searchParams), [searchParams]);
};

/**
 * Puts the search form back to its defaults, with the member's current
 * location prefilled.
 *
 * Runs on every entry to the search page — the side nav, the feature list, or
 * Back out of the results — because a procedure left over from a previous visit
 * reads as a search that is already set up when it never ran. The committed
 * search lives in the results URL, so nothing is lost by clearing the form:
 * `useHcbbSearch` hydrates it back out of the URL on the results page.
 */
export const useHcbbSearchReset = (): void => {
  const dispatch = useDispatch();
  const { location: currentLocation } = useUserLocation();
  const { targetLocation } = useSelector(
    (state: RootState) => state.priceTransparency,
  );

  // `LocationType` and `Place` carry the same fields, including `postal_code`;
  // naming the assignment here keeps that equivalence documented in one spot.
  const currentPlace: Place | null = currentLocation;

  // Read through a ref so a location the member clears by hand is not refilled
  // by the effect below — only an empty box waiting on geolocation is.
  const hasTargetLocationRef = useRef(Boolean(targetLocation));
  hasTargetLocationRef.current = Boolean(targetLocation);

  useEffect(() => {
    dispatch(setProcedure(null));
    dispatch(setDistance(HCBB_DEFAULT_DISTANCE));
    dispatch(setLocation(currentPlace));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Geolocation resolves after the first render, so the location lands late on
  // a cold start.
  useEffect(() => {
    if (!currentPlace || hasTargetLocationRef.current) return;
    dispatch(setLocation(currentPlace));
  }, [currentPlace, dispatch]);
};

/* ── the results view, and the detail screens ────────────────────────────── */

export type HcbbResultView = {
  /** Null until the member picks one — the results choose the first tab. */
  activeTab: HcbbResultTab | null;
  visibleFacilities: number;
  visibleBatches: number;
  /** Reveal depth into the *filtered* professional candidate list. */
  filteredVisibleBatches: number;
  setActiveTab: (tab: HcbbResultTab) => void;
  setVisibleFacilities: (count: number) => void;
  setVisibleBatches: (count: number) => void;
  setFilteredVisibleBatches: (count: number) => void;
};

/**
 * How much of each result list is revealed, kept across a trip to a detail
 * screen.
 *
 * Scoped by `resultKey`: a stored view belonging to another search never
 * matches, so the defaults are read instead and a new search always starts at
 * its first page. The slice is missing outright for a moment after redux-persist
 * rehydrates — it is deliberately not persisted — so the read is defended.
 */
export const useHcbbResultView = (resultKey: string): HcbbResultView => {
  const dispatch = useDispatch();
  const stored = useSelector((state: RootState) => state.hcbbResultView);
  const view = stored ?? INITIAL_HCBB_RESULT_VIEW;
  const isSameSearch = view.resultKey === resultKey;

  return {
    activeTab: isSameSearch
      ? view.activeTab
      : INITIAL_HCBB_RESULT_VIEW.activeTab,
    visibleFacilities: isSameSearch
      ? view.visibleFacilities
      : INITIAL_HCBB_RESULT_VIEW.visibleFacilities,
    visibleBatches: isSameSearch
      ? view.visibleBatches
      : INITIAL_HCBB_RESULT_VIEW.visibleBatches,
    filteredVisibleBatches: isSameSearch
      ? view.filteredVisibleBatches
      : INITIAL_HCBB_RESULT_VIEW.filteredVisibleBatches,
    setActiveTab: (tab) => dispatch(setHcbbActiveTab({ resultKey, tab })),
    setVisibleFacilities: (count) =>
      dispatch(setHcbbVisibleFacilities({ resultKey, count })),
    setVisibleBatches: (count) =>
      dispatch(setHcbbVisibleBatches({ resultKey, count })),
    setFilteredVisibleBatches: (count) =>
      dispatch(setHcbbFilteredVisibleBatches({ resultKey, count })),
  };
};

export type HcbbAdvancedFiltersView = {
  isPanelOpen: boolean;
  filters: HcbbAdvancedFilters;
  setPanelOpen: (isOpen: boolean) => void;
  setFilters: (filters: HcbbAdvancedFilters) => void;
  clearFilters: () => void;
};

/**
 * The advanced-search panel's open state and committed criteria, kept across
 * a trip to a detail screen the same way `useHcbbResultView` keeps paging
 * depth — scoped by `resultKey` for the same reason: a stored value
 * belonging to another search never matches, so a new search always opens
 * with the panel closed and no criteria applied.
 */
export const useHcbbAdvancedFilters = (
  resultKey: string,
): HcbbAdvancedFiltersView => {
  const dispatch = useDispatch();
  const stored = useSelector((state: RootState) => state.hcbbAdvancedFilters);
  const view = stored ?? INITIAL_HCBB_ADVANCED_FILTERS;
  const isSameSearch = view.resultKey === resultKey;

  return {
    isPanelOpen: isSameSearch
      ? view.isPanelOpen
      : INITIAL_HCBB_ADVANCED_FILTERS.isPanelOpen,
    filters: isSameSearch ? view.filters : INITIAL_HCBB_ADVANCED_FILTER_VALUES,
    setPanelOpen: (isOpen) =>
      dispatch(setHcbbAdvancedFiltersOpen({ resultKey, isOpen })),
    setFilters: (filters) =>
      dispatch(setHcbbAdvancedFilters({ resultKey, filters })),
    clearFilters: () => dispatch(clearHcbbAdvancedFilters({ resultKey })),
  };
};

/**
 * Opens a result row's detail screen.
 *
 * The row travels in router state and the search travels in the query string,
 * so Back lands on the member's own results and a detail URL opened cold has
 * somewhere to redirect to. The facility screen's doctor list also needs the
 * results page's own priced benchmark for its `majorGroup`, so that travels
 * alongside the facility rather than being re-fetched on the detail screen.
 */
export const useHcbbDetailNavigation = () => {
  const navigate = useNavigate();
  const { search } = useLocation();

  return {
    openFacilityDetail: (
      facility: HcbbFacilityResult,
      highlight: HcbbPriceHighlight | null,
    ) =>
      navigate(
        { pathname: HCBB_FACILITY_DETAIL_PATH, search },
        { state: { facility, highlight } },
      ),
    openProviderDetail: (professional: HcbbProfessionalResult) =>
      navigate(
        { pathname: HCBB_PROVIDER_DETAIL_PATH, search },
        { state: { professional } },
      ),
  };
};

/* ── recent searches ─────────────────────────────────────────────────────── */

export type HcbbRecentSearchList = {
  searches: HcbbRecentSearch[];
  isPending: boolean;
  /**
   * Warms the estimate this row will land on before handing back the results
   * URL — see the comment above `rerunSearch` inside the hook for why.
   */
  rerunSearch: (search: HcbbRecentSearch) => string;
};

/**
 * The searches this member has run before.
 *
 * No history and a failed request both render as the same "nothing yet"
 * empty state — the panel never fabricates rows to fill itself, since a
 * search the member never ran would lead nowhere real if they picked it.
 */
export const useHcbbRecentSearches = (): HcbbRecentSearchList => {
  const dispatch = useDispatch();
  const userInfo = useHcbbMemberInfo();
  const queryClient = useQueryClient();

  const query = useQuery<HcbbRecentSearch[], Error>({
    queryKey: [QueryKeys.HCBB_RECENT_SEARCH, userInfo.memberId],
    enabled: Boolean(userInfo.accessToken),
    // The server only needs to answer once per visit to the module — every
    // search the member runs afterwards is reflected locally, in place, by
    // `prependRecentSearch` (see `submitSearch` and `rerunSearch`), not by
    // asking the server again. A `staleTime: 0` here used to refetch on every
    // remount (e.g. Back from the results page), which raced the server's
    // own not-yet-consistent view of "just now" and reordered the list out
    // from under the member.
    staleTime: Infinity,
    // A failed request folds into the same empty state as no history, so a
    // silent retry buys nothing here.
    retry: false,
    queryFn: () => hcbbRecentSearches(userInfo),
  });

  /**
   * A recent search already carries everything `/estimate` needs, so the
   * call is made from here rather than left for the results page to
   * discover on its own — otherwise the member re-picks a search they
   * already ran only to watch the same loading skeleton play out again.
   * `prefetchQuery` writes into the exact cache entry
   * `useHcbbProcedureDetails` reads (`toHcbbEstimateQueryKey`), so the
   * results page mounts already priced; a prefetch failure is silently
   * discarded by design, and the results page's own query then runs for
   * real and renders its own error state.
   */
  const rerunSearch = (search: HcbbRecentSearch): string => {
    const hcbbSearch = toHcbbSearchFromRecent(search);

    // The results form reads the committed search back out of this slice
    // (see `useHcbbSearch`), and a recent-search pick never runs through the
    // form's own selects to populate it — unlike `submitSearch`, which
    // commits values the slice already holds. Without this, the search
    // page's own reset-on-arrival effect (`useHcbbSearchReset`) leaves the
    // slice pointing at the member's current location and the default
    // radius, and the results page silently shows those instead of the row
    // that was actually picked.
    dispatch(setProcedure(hcbbSearch.procedure));
    dispatch(setLocation(hcbbSearch.targetLocation));
    dispatch(setDistance(hcbbSearch.distance));

    queryClient.prefetchQuery({
      queryKey: toHcbbEstimateQueryKey(hcbbSearch, userInfo.memberId),
      queryFn: () => hcbbEstimate(hcbbSearch, userInfo),
    });
    // Moves the row to the top in place — it is already a well-formed
    // `HcbbRecentSearch`, so there is nothing to rebuild here.
    prependRecentSearch(queryClient, userInfo.memberId, search);

    return buildHcbbResultUrl(hcbbSearch);
  };

  return {
    searches: query.data ?? [],
    isPending: query.isPending && query.fetchStatus !== "idle",
    rerunSearch,
  };
};
